/**
 * The stylist's own members, on the assignment-scoped `/stylist/*` routes.
 *
 *   GET  /api/v1/stylist/dashboard                          assigned members, paged
 *   GET  /api/v1/stylist/members/{user_id}                  one assigned member, paged
 *
 * The member read carries the member's onboarding answers: a pinned summary
 * (style, sizes, fits avoided, budget, when the profile last changed), a flat
 * `style_profile` of whichever other answers are set, and `body_profile`.
 *
 * Every route derives the stylist from the bearer token; nothing here sends a
 * stylist id, so a member outside the assignment is a 403, never a leak.
 */

import {
  dashboardApiV1StylistDashboardGet,
  memberApiV1StylistMembersUserIdGet,
} from '@closet-theory/api';
import type { DashboardResponse, MemberDetail } from '@closet-theory/api';
import { configureStylistApi, handleUnauthorized } from './auth';

export const PAGE_SIZE = 100;
/** Safety stop for the dashboard walk: 50 pages of 100 members each. */
export const MAX_DASHBOARD_PAGES = 50;

type Result<T> = {
  data?: T | undefined;
  error?: unknown;
  response?: { status: number } | Response | undefined;
};

export type MemberApi = {
  dashboard(offset: number): Promise<Result<DashboardResponse>>;
  member(memberId: string, offset: number): Promise<Result<MemberDetail>>;
};

const generatedApi: MemberApi = {
  dashboard: (offset) => dashboardApiV1StylistDashboardGet({ query: { limit: PAGE_SIZE, offset } }),
  member: (memberId, offset) =>
    memberApiV1StylistMembersUserIdGet({
      path: { user_id: memberId },
      query: { limit: PAGE_SIZE, offset },
    }),
};

export type MemberService = {
  /**
   * Every assigned member, walked page by page so search and counts cover
   * them all. `members.length < total` only past `MAX_DASHBOARD_PAGES`.
   */
  dashboard(): Promise<DashboardResponse>;
  member(memberId: string, offset?: number): Promise<MemberDetail>;
};

function statusOf(result: Result<unknown>): number | undefined {
  return result.response?.status;
}

/** Plain sentences keyed by status, so a failure says what the stylist can do next. */
function settle<T>(
  result: Result<T>,
  onUnauthorized: (status?: number) => void,
  fallback: string,
  byStatus: Partial<Record<number, string>> = {},
): T | undefined {
  const status = statusOf(result);
  onUnauthorized(status);
  if (result.error !== undefined && result.error !== null) {
    throw new Error(
      (status !== undefined ? byStatus[status] : undefined) ??
        `${fallback}${status ? ` · HTTP ${status}` : ''}`,
    );
  }
  if (status !== undefined && status >= 400) {
    throw new Error(byStatus[status] ?? `${fallback} · HTTP ${status}`);
  }
  return result.data;
}

function required<T>(value: T | undefined, fallback: string): T {
  if (value === undefined || value === null) throw new Error(fallback);
  return value;
}

const NOT_ASSIGNED = 'This member is not assigned to you, so their details are hidden.';

export function createMemberService(
  api: MemberApi = generatedApi,
  configure: () => void = configureStylistApi,
  onUnauthorized: (status?: number) => void = handleUnauthorized,
): MemberService {
  return {
    async dashboard() {
      configure();
      const failure = 'Your members could not be loaded.';
      const first = required(settle(await api.dashboard(0), onUnauthorized, failure), failure);
      const members = [...first.members];
      const total = first.total ?? members.length;
      for (let page = 1; page < MAX_DASHBOARD_PAGES && members.length < total; page += 1) {
        const next = required(
          settle(await api.dashboard(members.length), onUnauthorized, failure),
          failure,
        );
        if (next.members.length === 0) break;
        members.push(...next.members);
      }
      return { ...first, members, total };
    },
    async member(memberId, offset = 0) {
      configure();
      const failure = "This member's details could not be loaded.";
      return required(
        settle(await api.member(memberId, offset), onUnauthorized, failure, { 403: NOT_ASSIGNED }),
        failure,
      );
    },
  };
}

/** The member's email from the untyped user record; undefined when it is not sent. */
export function memberEmail(detail: Pick<MemberDetail, 'user'>): string | undefined {
  const value = detail.user?.['email'];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export const memberService = createMemberService();
